import { defineComponent as _defineComponent } from 'vue'
import { useServiceStore } from "@/stores/serviceStore";
import { PropType, computed, onMounted, provide, ref } from "vue";
import {
  IOption,
  PROVIDE_KEY_SET_FORM_VALUES,
  PROVIDE_KEY_SET_MYINFO_FIELDS,
  RankCode,
  SESSION_KEY_FORM_VALUES,
  SESSION_KEY_MYINFO_FIELDS,
  TimerStatus,
} from "@/types";
import { Form } from "vee-validate";
import DynamicFormField from "@/components/signup/DynamicFormField.vue";
import BaseButton from "@/components/shared/button/base/BaseButton.vue";
import { ISignupField } from "@/domain/entities/serviceInfo/serviceInfo.types";
import { IUserInfo } from "@/domain/entities/user";


const __default__ = {
  name: "DynamicEditMyInfoForm",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  rankCode: {
    type: String as PropType<RankCode>,
    required: true,
  },
  userInfo: {
    type: Object as PropType<IUserInfo>,
  },
},
  emits: ["onClickEditMyInfoBtn"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const serviceStore = useServiceStore();

const isDisabledField = (field: ISignupField) => {
  return field.authRequiredForEditYn || !field.isEditable || !field.isValid;
};

const hasUnverifiedFields = () => {
  return !!serviceStore.getUnverifiedFields(props.rankCode);
};

const getSchema = (field: ISignupField): Record<string, any> => {
  const passwordPlaceholder = field.placeholder
    ?.split("(")?.[1]
    ?.replace(")", "");
  const passwordErrorMessage = passwordPlaceholder
    ? passwordPlaceholder + "으로 입력해주세요."
    : "비밀번호 양식에 맞게 입력해주세요.";

  const parentField = getParentField(field);
  const parentFieldValue =
    formValues.value[parentField?.inputFields?.[0] ?? ""] ?? "";

  const childField = getChildField(field);
  const childFieldValue =
    formValues.value[childField?.inputFields?.[0] ?? ""] ?? "";

  return {
    "JOIN.MEMBER_NAME": { required: "이름을" },
    "JOIN.CLIENT_COMPANY": { required: "소속사를" },
    "JOIN.WORK_TIME": { required: "근무형태를" },
    "JOIN.LOGINID": { required: "아이디를" },
    "JOIN.MEMBER_EMAIL": { required: "이메일을", email: true },
    "JOIN.MEMBER_EMPLOYEE_NO": { required: "사번을" },
    "JOIN.MEMBER_PHONE": { required: "휴대폰 번호를", phone: true },
    "JOIN.MEMBER_DEPT_NAME": { required: "부서를" },
    "JOIN.HOME_ADDRESS": { required: "자택 주소를" },
    "JOIN.DUTY_MANAGER_NAME": { required: "이름을" },
    "JOIN.DUTY_MANAGER_DEPT_NAME": { required: "부서를" },
    "JOIN.BP_NAME": { required: "협력사 이름을" },
    "JOIN.DUTY_MANAGER_EMAIL": { required: "이메일을", email: true },
    "JOIN.CLIENT_COMPANY_BP": { required: "근무회사를" },
    "JOIN.DUTY_NOTE": { required: "담당업무를" },
    "JOIN.DUTY_MANAGER_CLIENT_COMPANY": { required: "소속회사를" },
    "JOIN.DUTY_MANAGER_PHONE": { required: "연락처를" },
    "JOIN.PASSWORD": {
      dependenciesRequired: ["새 비밀번호를", parentFieldValue],
      password: [field.passwordRegex, passwordErrorMessage],
      newPassword: parentFieldValue,
    },
    currentPassword: {
      dependenciesRequired: ["현재 비밀번호를", childFieldValue],
      password: [field.passwordRegex, passwordErrorMessage],
    },
    emailCertNo: { required: "인증번호를" },
    passwordCert: {
      dependenciesRequired: ["새 비밀번호를 다시", parentFieldValue],
      passwordConfirm: parentFieldValue,
    },
    phoneCertNo: { required: "인증번호를" },
  };
};

const getRules = (field: ISignupField) => {
  if (!field.isRequired || !field.isEditable || !field.key) return;

  const parentField = getParentField(field);

  const isCertNoField =
    field.key === "emailCertNo" || field.key === "phoneCertNo";
  if (isCertNoField && !parentField?.isEdited) return;

  const schema = getSchema(field);
  return schema[field.key];
};

const myInfoFields = computed(
  () =>
    serviceStore.signupInfos.find((info) => info.useRankCode === props.rankCode)
      ?.fields
);

const getCurrentPasswordField = (field: ISignupField) => {
  const currentPasswordField = ref<ISignupField>({
    ...field,
    key: "currentPassword",
    inputFields: ["currPassword"],
    label: "현재 비밀번호",
    placeholder: "현재 비밀번호를 입력하세요",
  });

  return currentPasswordField.value;
};

const getNextPasswordField = (field: ISignupField) => {
  const nextPasswordField = ref<ISignupField>({
    ...field,
    inputFields: ["nextPassword"],
    label: "새 비밀번호",
    parentFieldKey: "currentPassword",
  });

  return nextPasswordField.value;
};

const getNextPasswordCertField = (field: ISignupField) => {
  const nextPasswordCertField = ref<ISignupField>({
    ...field,
    inputFields: ["nextPasswordCert"],
    label: "새 비밀번호 확인",
  });

  return nextPasswordCertField.value;
};

const getComputedMyInfoFields = () => {
  const filteredMyInfoFields: ISignupField[] = [];

  myInfoFields.value?.forEach((field) => {
    if (field.key?.includes("DUTY_MANAGER")) return;
    if (field.type === "label") return;
    if (field.excludeUserInfoYn) return;

    switch (field.key) {
      case "JOIN.PASSWORD": {
        filteredMyInfoFields.push(getCurrentPasswordField(field));
        filteredMyInfoFields.push(getNextPasswordField(field));
        break;
      }
      case "passwordCert": {
        filteredMyInfoFields.push(getNextPasswordCertField(field));
        break;
      }
      default:
        filteredMyInfoFields.push(field);
        break;
    }
  });

  return filteredMyInfoFields;
};

const fieldsInSessionStorage = window.sessionStorage.getItem(
  SESSION_KEY_MYINFO_FIELDS
);

const arrangedMyInfoFields = computed(() => {
  if (fieldsInSessionStorage) {
    const fields = JSON.parse(fieldsInSessionStorage) as ISignupField[];
    window.sessionStorage.removeItem(SESSION_KEY_MYINFO_FIELDS);
    return fields;
  }
  return getComputedMyInfoFields();
});

const setMyInfoFieldsToSessionStorage = () => {
  window.sessionStorage.setItem(
    SESSION_KEY_MYINFO_FIELDS,
    JSON.stringify(arrangedMyInfoFields.value)
  );
};
provide(PROVIDE_KEY_SET_MYINFO_FIELDS, setMyInfoFieldsToSessionStorage);

const formValuesInSessionStorage = window.sessionStorage.getItem(
  SESSION_KEY_FORM_VALUES
);

const formValues = ref<Record<string, any>>({});

const setFormValuesToSessionStorage = () => {
  window.sessionStorage.setItem(
    SESSION_KEY_FORM_VALUES,
    JSON.stringify(formValues.value)
  );
};
provide(PROVIDE_KEY_SET_FORM_VALUES, setFormValuesToSessionStorage);

const initFormValues = () => {
  // 본인인증 후 돌아온 경우
  if (formValuesInSessionStorage) {
    window.sessionStorage.removeItem(SESSION_KEY_FORM_VALUES);
    formValues.value = JSON.parse(formValuesInSessionStorage);

    // 본인인증에서 받아온 데이터(휴대폰번호 등) 갱신
    const editedFields = arrangedMyInfoFields.value.filter(
      (field) => field.authRequiredForEditYn
    );
    editedFields.forEach((field: ISignupField) => {
      if (!field.inputFields?.length) return;
      const value = props.userInfo?.[field.inputFields[0]];
      updateFormValue(field, value);
    });
    return;
  }

  arrangedMyInfoFields.value.forEach((field) => {
    if (!field.inputFields?.length) return;
    const value = props.userInfo?.[field.inputFields[0]];
    updateFormValue(field, value);
  });
};

const getFieldValue = (inputFields?: string[]) => {
  return inputFields ? formValues.value[inputFields[0]] : undefined;
};

const getParentFieldValue = (field: ISignupField) => {
  const parentField = getParentField(field);
  if (!parentField || !parentField.inputFields) {
    return "";
  }
  return formValues.value[parentField.inputFields[0]];
};

const getParentField = (field: ISignupField) => {
  return arrangedMyInfoFields.value?.find(
    (myInfoField) => myInfoField.key && myInfoField.key === field.parentFieldKey
  );
};

const getChildField = (field: ISignupField) => {
  return arrangedMyInfoFields.value?.find(
    (myInfoField) =>
      myInfoField.parentFieldKey && myInfoField.parentFieldKey === field.key
  );
};

const parseSubOptions = (data: any): IOption => {
  const option: IOption = {
    name: data,
    value: data,
  };
  return option;
};

const updateChildFieldOptions = (field: ISignupField, value: any) => {
  const selectedOption = field.options?.find(
    (option) => option.value === value
  );
  const childField = getChildField(field);
  if (!selectedOption?.data || !childField?.key || !childField.subOptionsKey)
    return;
  serviceStore.updateFieldOptions(
    props.rankCode,
    childField.key,
    selectedOption.data[childField.subOptionsKey].map(parseSubOptions)
  );
};

const updateValue = (field: ISignupField, value: any) => {
  updateFormValue(field, value);
  updateFieldEdited(field, value);
};

const updateFormValue = (field: ISignupField | undefined, value: any) => {
  if (!field?.inputFields?.length) return;

  field.inputFields.forEach((inputField) => {
    formValues.value[inputField] = value;
  });

  if (field.type !== "option") return;

  updateChildFieldOptions(field, value);
};

const updateChildTimerStatus = (
  field: ISignupField,
  timerStatus: TimerStatus
) => {
  const childField = getChildField(field);
  serviceStore.updateFieldTimerStatus(
    props.rankCode ?? "",
    childField?.key ?? "",
    timerStatus
  );
};

const updateFieldEdited = (field: ISignupField, newValue: any) => {
  const preValue = props.userInfo?.[field.inputFields?.[0] ?? ""];
  const isEdited = preValue !== newValue;
  serviceStore.updateFieldEdited(
    props.rankCode ?? "",
    field?.key ?? "",
    isEdited
  );
};

const getFilteredFormValues = (fields: ISignupField[]) => {
  const values: Record<string, any> = {};
  fields.forEach((field) => {
    field.inputFields?.forEach((key) => {
      values[key] = formValues.value[key];
    });
  });

  return values;
};

const onClickEditMyInfoBtn = () => {
  // 비밀번호 변경 관련 데이터
  const passwordFields = arrangedMyInfoFields.value.filter(
    (field) => field.isEdited && field.type?.includes("password")
  );
  const passwordValues = getFilteredFormValues(passwordFields);

  // 비밀번호를 제외한 나머지 사용자 정보 데이터
  const remainingEditedFields = arrangedMyInfoFields.value.filter(
    (field) => field.isEdited && !field.type?.includes("password")
  );
  const remainingEditedValues = getFilteredFormValues(remainingEditedFields);

  emit("onClickEditMyInfoBtn", passwordValues, remainingEditedValues);
};

onMounted(() => {
  initFormValues();
});

const __returned__ = { props, emit, serviceStore, isDisabledField, hasUnverifiedFields, getSchema, getRules, myInfoFields, getCurrentPasswordField, getNextPasswordField, getNextPasswordCertField, getComputedMyInfoFields, fieldsInSessionStorage, arrangedMyInfoFields, setMyInfoFieldsToSessionStorage, formValuesInSessionStorage, formValues, setFormValuesToSessionStorage, initFormValues, getFieldValue, getParentFieldValue, getParentField, getChildField, parseSubOptions, updateChildFieldOptions, updateValue, updateFormValue, updateChildTimerStatus, updateFieldEdited, getFilteredFormValues, onClickEditMyInfoBtn, get Form() { return Form }, DynamicFormField, BaseButton }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})